import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, HStack, VStack, Text, Badge, Alert, AlertIcon, Button, Code, Link, Spinner, Textarea,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
} from "@chakra-ui/react";
import { ImpactChip } from "./StatusDonut";
import { byImpact, findingImpact, notSized } from "./impact";

/**
 * The nightly AI review's findings, shared by the three issue tabs. Each night the
 * nightly-code-review cron (backend/api/code_review/nightly.py) labels what it finds in the day's
 * commits with an area, and each tab shows its own: memory → Memory Issues, database → DB Health
 * Check, general → Code Review. A finding stays open (public.code_findings) until a later commit
 * fixes it or someone dismisses it with a reason; either way it moves to "Recently fixed or
 * dismissed".
 */

export const SEVERITY = {
  problem: { label: "PROBLEM", scheme: "red" },
  warning: { label: "WARNING", scheme: "orange" },
};

// What a task made from a finding is called, and where it's filed, per tab.
export const AREA_TASK = {
  general: { prefix: "Code review (AI)", sourceKind: "code_review" },
  memory: { prefix: "Memory issue (AI)", sourceKind: "memory_issue" },
  database: { prefix: "Database (AI)", sourceKind: "db_health" },
};

export function nightLabel(isoDate) {
  if (!isoDate) return "";
  const d = new Date(`${String(isoDate).slice(0, 10)}T12:00:00`);
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

/** Title and description for a task made from one AI finding (`run` is its night, when known). */
export function aiFindingTaskText(finding, run) {
  const area = AREA_TASK[finding.area] || AREA_TASK.general;
  const lines = [`${(SEVERITY[finding.severity] || SEVERITY.warning).label}: ${finding.title}`];
  if (finding.explanation) lines.push(`What's wrong: ${finding.explanation}`);
  if (finding.fix) lines.push(`What to do: ${finding.fix}`);
  lines.push(`Where: ${finding.file}${finding.line ? `:${finding.line}` : ""}`);
  lines.push(`Commit: ${finding.short}${finding.commit_message ? ` "${finding.commit_message}"` : ""}`
             + `${finding.author ? ` by ${finding.author}` : ""}${finding.commit_url ? ` (${finding.commit_url})` : ""}`);
  if (finding.owners?.length) lines.push(`Suggested to fix: ${finding.owners.join(", ")}`);
  const night = finding.first_seen || run?.review_date;
  lines.push(`Found by the nightly AI review of ${night || "the day's commits"} · finding id ${finding.id}`);
  return { title: `${area.prefix}: ${finding.title}`, description: lines.join("\n") };
}

function Closed({ finding }) {
  if (!finding.status || finding.status === "open") return null;
  const fixed = finding.status === "fixed";
  return (
    <Text fontSize="xs" color={fixed ? "green.700" : "gray.600"} mb={1} data-testid={`ai-finding-closed-${finding.id}`}>
      {fixed ? "Fixed" : `Dismissed${finding.resolved_by ? ` by ${finding.resolved_by}` : ""}`}
      {finding.resolved_at ? ` ${new Date(finding.resolved_at).toLocaleDateString()}` : ""}
      {finding.resolved_note ? `: ${finding.resolved_note}` : ""}
    </Text>
  );
}

// `impact` replaces the finding's own (the Memory Issues tab names the plan it's a share of).
export function AiFindingCard({ finding, task, canAssign, onAssign, onDismiss, TaskChip, badge = null, impact = null }) {
  const meta = SEVERITY[finding.severity] || SEVERITY.warning;
  const open = !finding.status || finding.status === "open";
  return (
    <Box borderWidth="1px" borderLeftWidth="4px" borderLeftColor={open ? `${meta.scheme}.400` : "gray.300"}
         borderRadius="md" p={3} data-testid={`ai-finding-${finding.id}`} data-status={finding.severity}>
      <HStack justify="space-between" align="flex-start" spacing={2} mb={1}>
        <HStack spacing={2} flexWrap="wrap">
          {badge}
          <Badge colorScheme={open ? meta.scheme : "gray"}>{meta.label}</Badge>
          <Text fontWeight="bold">{finding.title}</Text>
          <Badge variant="outline" colorScheme="purple" fontSize="2xs" title="Claude reviews each day's commits after midnight">
            NIGHTLY AI REVIEW
          </Badge>
          <ImpactChip impact={impact || findingImpact(finding)} testId={`ai-finding-impact-${finding.id}`} />
        </HStack>
        {open && (
          <HStack spacing={1} flexShrink={0}>
            {onDismiss && (
              <Button size="xs" variant="ghost" data-testid={`ai-finding-dismiss-${finding.id}`}
                      onClick={() => onDismiss(finding)}>
                Dismiss
              </Button>
            )}
            {onAssign && (
              <Button size="xs" variant="outline" data-testid={`ai-finding-assign-${finding.id}`}
                      isDisabled={!canAssign} onClick={() => onAssign(finding)}>
                Assign task
              </Button>
            )}
          </HStack>
        )}
      </HStack>
      <HStack spacing={2} flexWrap="wrap" fontSize="xs" color="gray.600" mb={1}>
        <Code fontSize="xs">{finding.file}{finding.line ? `:${finding.line}` : ""}</Code>
        <Text>in</Text>
        {finding.commit_url
          ? <Link href={finding.commit_url} isExternal color="blue.600">{finding.short}</Link>
          : <Code fontSize="xs">{finding.short}</Code>}
        {finding.commit_message && <Text noOfLines={1}>“{finding.commit_message}”</Text>}
        {finding.first_seen && <Text>· found {nightLabel(finding.first_seen)}</Text>}
      </HStack>
      <Closed finding={finding} />
      {open && finding.owners?.length > 0 && (
        <Text fontSize="xs" color="red.700" mb={1} title="The author of the commit that made the change">
          Suggested to fix: {finding.owners.join(", ")}
        </Text>
      )}
      {task && TaskChip && <Box mb={2}><TaskChip task={task} testId={`ai-finding-task-${finding.id}`} /></Box>}
      {finding.explanation && <Text fontSize="sm" mb={1}>{finding.explanation}</Text>}
      {open && finding.fix && (
        <Box>
          <Text fontSize="xs" fontWeight="bold" color="gray.600">WHAT TO DO</Text>
          <Text fontSize="sm">{finding.fix}</Text>
        </Box>
      )}
    </Box>
  );
}

const REASON_MAX = 500;

export function DismissDialog({ finding, onClose, onDismiss }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const trimmed = reason.trim();

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onDismiss(finding, trimmed);
      onClose();
    } catch (e) {
      setError(e?.message || String(e));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent data-testid="ai-finding-dismiss-dialog">
        <ModalHeader>Dismiss this finding?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" mb={2}>
            <b>{finding.title}</b>. It leaves the list for everyone. Say why it isn't a problem, so whoever sees it
            later knows.
          </Text>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={REASON_MAX} rows={3}
                    placeholder="e.g. The cache is cleared every night, so it can't grow without limit."
                    data-testid="ai-finding-dismiss-reason" />
          <Text fontSize="xs" color="gray.500" mt={1}>{trimmed.length}/{REASON_MAX}</Text>
          {error && <Alert status="error" mt={2} fontSize="sm" borderRadius="md"><AlertIcon />{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button colorScheme="red" isDisabled={!trimmed} isLoading={saving} onClick={submit}
                  data-testid="ai-finding-dismiss-confirm">
            Dismiss
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * One tab's AI findings: the open ones, the recently closed ones on request, and dismissing.
 * Returns the dismiss dialog to render (null when closed).
 */
export function useCodeFindings(area, fetchFindings, dismissFinding) {
  const [state, setState] = useState({ loading: Boolean(fetchFindings), findings: [] });
  const [closed, setClosed] = useState(null);     // null until "Recently fixed or dismissed" is opened
  const [dismissing, setDismissing] = useState(null);

  const reload = useCallback(() => {
    if (!fetchFindings) return Promise.resolve();
    return fetchFindings({ area, status: "open" })
      .then((data) => setState({ findings: data.findings || [], missing: data.missing || null }))
      .catch((e) => setState({ findings: [], error: e?.message || String(e) }));
  }, [area, fetchFindings]);

  useEffect(() => { reload(); }, [reload]);

  const toggleClosed = useCallback(() => {
    if (closed) { setClosed(null); return; }
    setClosed({ loading: true, findings: [] });
    Promise.all(["fixed", "dismissed"].map((status) => fetchFindings({ area, status })))
      .then(([fixed, dismissed]) => {
        const all = [...(fixed.findings || []), ...(dismissed.findings || [])];
        all.sort((a, b) => String(b.resolved_at || "").localeCompare(String(a.resolved_at || "")));
        setClosed({ findings: all });
      })
      .catch((e) => setClosed({ findings: [], error: e?.message || String(e) }));
  }, [area, closed, fetchFindings]);

  const dismiss = useCallback(async (finding, reason) => {
    await dismissFinding(finding.id, reason);
    setState((s) => ({ ...s, findings: s.findings.filter((f) => f.id !== finding.id) }));
    setClosed(null);
  }, [dismissFinding]);

  const dismissDialog = dismissing
    ? <DismissDialog finding={dismissing} onClose={() => setDismissing(null)} onDismiss={dismiss} />
    : null;

  return {
    ...state, closed, toggleClosed, reload,
    askDismiss: dismissFinding ? setDismissing : null,
    dismissDialog,
  };
}

const SIZING_POLL_MS = 3000;

/**
 * Sizing the nightly review's findings saved without an impact, on a tab's manual run: sizeNow() asks
 * the server to size them (it does every tab's at once, in one short Claude request), follows it and
 * reloads this tab's findings when it's done. Does nothing when this tab has none to size.
 */
export function useSizing(found, startSizing, loadSizing, pollMs) {
  const every = pollMs ?? SIZING_POLL_MS;
  const [sizing, setSizing] = useState(null);     // null | {state: running | done | failed, asked, estimated, error}
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const unsized = (found.findings || []).filter((f) => notSized({ impact: findingImpact(f) })).length;
  const reload = found.reload;
  const sizeNow = useCallback(async () => {
    if (!startSizing || !loadSizing || !unsized) return;
    setSizing({ state: "running", asked: unsized });
    try {
      let status = await startSizing();
      while (status?.state === "running") {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, every));
        if (!mounted.current) return;
        // eslint-disable-next-line no-await-in-loop
        status = await loadSizing();
      }
      if (!mounted.current) return;
      if (status?.state === "failed") {
        setSizing({ state: "failed", error: status.error || "it stopped without saying why" });
        return;
      }
      setSizing({ state: "done", estimated: status?.result?.estimated ?? 0 });
      if (reload) await reload();
    } catch (e) {
      if (mounted.current) setSizing({ state: "failed", error: e?.message || String(e) });
    }
  }, [startSizing, loadSizing, unsized, every, reload]);
  return { sizing, sizeNow, unsized };
}

function count(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

/** What the sizing is doing, under a tab's list of what to fix. */
export function SizingNote({ sizing, testId }) {
  if (!sizing) return null;
  if (sizing.state === "running") {
    return (
      <HStack spacing={2} data-testid={testId} data-state="running">
        <Spinner size="xs" />
        <Text fontSize="xs" color="gray.600">
          Claude is sizing the {count(sizing.asked, "finding")} saved without an impact…
        </Text>
      </HStack>
    );
  }
  if (sizing.state === "failed") {
    return (
      <Text fontSize="xs" color="red.600" data-testid={testId} data-state="failed">
        Couldn't size the findings saved without an impact: {sizing.error}
      </Text>
    );
  }
  return (
    <Text fontSize="xs" color="green.700" data-testid={testId} data-state="done">
      Sized {count(sizing.estimated, "finding")} saved without an impact.
    </Text>
  );
}

/** The list a tab shows under "From the nightly code review", biggest impact first. */
export function AiFindingsList({ findings, taskByRef = {}, canAssign, onAssign, onDismiss, TaskChip, empty }) {
  if (!findings.length) {
    return empty ? <Text fontSize="sm" color="gray.600">{empty}</Text> : null;
  }
  return (
    <VStack align="stretch" spacing={2}>
      {byImpact(findings, (f) => findingImpact(f).pct).map((f) => (
        <AiFindingCard key={f.id} finding={f} task={taskByRef[f.id]} canAssign={canAssign} onAssign={onAssign}
                       onDismiss={onDismiss} TaskChip={TaskChip} />
      ))}
    </VStack>
  );
}

/** "Recently fixed or dismissed" toggle and list. */
export function ClosedFindings({ closed, toggleClosed, testId }) {
  return (
    <Box data-testid={testId}>
      <Button size="xs" variant="link" colorScheme="blue" onClick={toggleClosed}>
        {closed ? "Hide fixed and dismissed findings" : "Show recently fixed and dismissed findings"}
      </Button>
      {closed?.loading && <Spinner size="xs" ml={2} />}
      {closed?.error && <Text fontSize="xs" color="red.600">{closed.error}</Text>}
      {closed && !closed.loading && !closed.error && (
        <VStack align="stretch" spacing={2} mt={2}>
          {closed.findings.length
            ? closed.findings.map((f) => <AiFindingCard key={f.id} finding={f} />)
            : <Text fontSize="sm" color="gray.600">None yet.</Text>}
        </VStack>
      )}
    </Box>
  );
}

/** A whole "From the nightly code review" section for the DB Health Check and Code Review tabs.
 * `found` is the tab's own useCodeFindings() when it needs the findings too (for its health). */
/**
 * What a tab says about the nightly review's findings when the open ones are already in its main list,
 * most important first: where they come from, the fixed and dismissed ones, and why they can't load.
 */
export function AiFindingsNote({ found, title, blurb, testId }) {
  return (
    <Box data-testid={testId}>
      <Text fontWeight="bold" fontSize="sm">{title}</Text>
      {blurb && <Text fontSize="xs" color="gray.600" mb={2}>{blurb}</Text>}
      {found.loading && <Spinner size="sm" />}
      {found.error && <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />Couldn't load them: {found.error}</Alert>}
      {found.missing && <Alert status="info" borderRadius="md" fontSize="sm"><AlertIcon />{found.missing}</Alert>}
      {!found.loading && !found.error && !found.missing && (
        <ClosedFindings closed={found.closed} toggleClosed={found.toggleClosed} testId={`${testId}-closed`} />
      )}
    </Box>
  );
}

export function AiFindingsSection({ area, title, blurb, fetchFindings, dismissFinding, taskByRef, canAssign,
                                    onAssign, TaskChip, testId, found: tabFound = null }) {
  const own = useCodeFindings(area, tabFound ? null : fetchFindings, dismissFinding);
  const found = tabFound || own;
  const problems = found.findings.filter((f) => f.severity === "problem").length;
  return (
    <Box data-testid={testId}>
      <HStack spacing={2} mb={1} flexWrap="wrap">
        <Text fontWeight="bold">{title}</Text>
        {!found.loading && !found.missing && (
          <Badge colorScheme={found.findings.length ? (problems ? "red" : "orange") : "green"}
                 data-testid={`${testId}-count`}>
            {found.findings.length ? `${plural(found.findings.length, "open finding")}` : "nothing open"}
          </Badge>
        )}
      </HStack>
      {blurb && <Text fontSize="xs" color="gray.600" mb={2}>{blurb}</Text>}
      {found.loading && <Spinner size="sm" />}
      {found.error && <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />Couldn't load them: {found.error}</Alert>}
      {found.missing && <Alert status="info" borderRadius="md" fontSize="sm"><AlertIcon />{found.missing}</Alert>}
      {!found.loading && !found.error && !found.missing && (
        <VStack align="stretch" spacing={2}>
          <AiFindingsList findings={found.findings} taskByRef={taskByRef} canAssign={canAssign} onAssign={onAssign}
                          onDismiss={found.askDismiss} TaskChip={TaskChip}
                          empty="Nothing open: the nightly review hasn't found anything here, or it's all been fixed." />
          <ClosedFindings closed={found.closed} toggleClosed={found.toggleClosed} testId={`${testId}-closed`} />
        </VStack>
      )}
      {found.dismissDialog}
    </Box>
  );
}
